/**
 * CR383/CR384/CR465: S0.5 Collection Step Summary Tile
 * Shows S0.5 status in the Collection Steps section of BatchRunStepsPanel.
 * CR384: Mode-aware — always renders when flyer source is active.
 * CR465: Shows download run status + job progress when in Download mode.
 */

import { useState, useEffect } from "react";
import { Download, Settings, CheckCircle, AlertCircle, Loader2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import type { S05FlyerFetchMode } from "@/lib/flyer-bucket/types";
import type { FlyerCaptureMode } from "./s0-settings/types";
import { FLYER_CAPTURE_MODES } from "./s0-settings/types";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface S05SourceInfo {
  id: string;
  name: string;
  s0_flyer_fetch_enabled: boolean;
  s0_flyer_fetch_mode: S05FlyerFetchMode;
}

interface DownloadRunStatus {
  id: string;
  status: string;
  sources_total: number;
  sources_succeeded: number;
  sources_failed: number;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
}

interface S05CollectionStepTileProps {
  flyerBucketSourceId?: string | null;
  flyerCaptureMode?: FlyerCaptureMode;
  /** CR465: batch_run_id to fetch download run status */
  batchRunId?: string;
  autoModeEnabled?: boolean;
  onToggleAutoMode?: (enabled: boolean) => void;
  isOrchestrating?: boolean;
}

const MODE_LABELS: Record<S05FlyerFetchMode, string> = {
  DOWNLOAD_ONLY: "Download",
  IMAGE_ONLY: "Screenshot",
  IMAGE_AND_DOWNLOAD: "Both",
};

const STATUS_BADGE: Record<string, { variant: "default" | "outline" | "destructive" | "secondary"; icon: typeof CheckCircle }> = {
  QUEUED: { variant: "secondary", icon: Loader2 },
  RUNNING: { variant: "default", icon: Loader2 },
  COMPLETE: { variant: "outline", icon: CheckCircle },
  FAILED: { variant: "destructive", icon: XCircle },
};

export function S05CollectionStepTile({
  flyerBucketSourceId,
  flyerCaptureMode = 'DOWNLOAD_ONLY',
  batchRunId,
  autoModeEnabled = false,
  onToggleAutoMode,
  isOrchestrating = false,
}: S05CollectionStepTileProps) {
  const [sources, setSources] = useState<S05SourceInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadRun, setDownloadRun] = useState<DownloadRunStatus | null>(null);

  useEffect(() => {
    const fetchSources = async () => {
      let query = supabase
        .from("flyer_bucket_sources")
        .select("id, name, s0_flyer_fetch_enabled, s0_flyer_fetch_mode")
        .eq("is_enabled", true);

      if (flyerBucketSourceId) {
        query = query.eq("id", flyerBucketSourceId);
      }

      const { data, error } = await query;
      if (!error && data) {
        setSources(data as S05SourceInfo[]);
      }
      setLoading(false);
    };

    const fetchDownloadRun = async () => {
      if (!batchRunId) return;
      const { data } = await supabase
        .from("flyer_download_runs")
        .select("id, status, sources_total, sources_succeeded, sources_failed, started_at, completed_at, error")
        .eq("batch_run_id", batchRunId)
        .maybeSingle();
      if (data) setDownloadRun(data as DownloadRunStatus);
    };

    fetchSources();
    fetchDownloadRun();
  }, [flyerBucketSourceId, batchRunId]);

  if (loading) return null;

  const enabledSources = sources.filter((s) => s.s0_flyer_fetch_enabled);
  const captureLabel = FLYER_CAPTURE_MODES.find(m => m.value === flyerCaptureMode)?.label || flyerCaptureMode;
  const isPlannedMode = flyerCaptureMode !== 'DOWNLOAD_ONLY';

  const statusInfo = downloadRun ? STATUS_BADGE[downloadRun.status] : null;
  const StatusIcon = statusInfo?.icon || Settings;

  return (
    <div className={`rounded-lg border bg-card p-3 ${isPlannedMode ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Download className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">S0.5: Flyer Download / Capture</h3>
        </div>
        <div className="flex items-center gap-1.5">
          {isOrchestrating && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
          {onToggleAutoMode && (
            <div className="flex items-center gap-2 mr-2 border-r pr-2">
              <Label htmlFor="s05-auto" className="text-[10px] text-muted-foreground uppercase">Auto</Label>
              <Switch
                id="s05-auto"
                checked={autoModeEnabled}
                onCheckedChange={onToggleAutoMode}
                className="scale-75"
              />
            </div>
          )}
          {downloadRun && statusInfo && (
            <Badge variant={statusInfo.variant} className="text-xs gap-1">
              <StatusIcon className={`h-3 w-3 ${downloadRun.status === 'RUNNING' || downloadRun.status === 'QUEUED' ? 'animate-spin' : ''}`} />
              {downloadRun.status}
            </Badge>
          )}
          <Badge variant="outline" className="text-xs gap-1">
            <Settings className="h-3 w-3" />
            {captureLabel}
          </Badge>
        </div>
      </div>

      {isPlannedMode && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Image / Both modes are not yet wired. Pipeline still runs standard PDF download → render → OCR.
        </p>
      )}

      {!isPlannedMode && !downloadRun && (
        <p className="text-xs text-muted-foreground mt-1">
          Python-powered pre-fetch before S1.0/S1.1 (download and/or screenshots).
        </p>
      )}

      {/* CR465: Download run progress */}
      {downloadRun && (
        <div className="mt-2 space-y-1">
          <div className="flex items-center gap-3 text-xs">
            <span className="text-muted-foreground">
              Sources: {downloadRun.sources_total}
            </span>
            {downloadRun.sources_succeeded > 0 && (
              <span className="text-green-600 dark:text-green-400 flex items-center gap-0.5">
                <CheckCircle className="h-3 w-3" />
                {downloadRun.sources_succeeded} OK
              </span>
            )}
            {downloadRun.sources_failed > 0 && (
              <span className="text-red-600 dark:text-red-400 flex items-center gap-0.5">
                <XCircle className="h-3 w-3" />
                {downloadRun.sources_failed} failed
              </span>
            )}
          </div>
          {downloadRun.error && (
            <p className="text-xs text-destructive mt-1">{downloadRun.error}</p>
          )}
        </div>
      )}

      {/* Legacy S0.5 bucket source info */}
      {!downloadRun && (
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          {enabledSources.length > 0 ? (
            enabledSources.map((s) => (
              <span
                key={s.id}
                className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5"
              >
                <CheckCircle className="h-3 w-3 text-primary" />
                {s.name}: {MODE_LABELS[s.s0_flyer_fetch_mode] || s.s0_flyer_fetch_mode}
              </span>
            ))
          ) : (
            <span className="text-muted-foreground italic">
              No S0.5 worker configuration yet. Using legacy S1.0 Collect path.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
